import { useQuery, useQueryClient } from 'react-query';

import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api/client';
import { type IListing } from 'src/services/api/listings/types';

export type ListingsDateRange = 'today' | 'week' | 'month';

type IGetMatchedListingsOptions = {
    propertyId?: number;
    status?: 'active' | 'inactive';
    page?: number;
    pageSize?: number;
    disabled?: boolean;
    refetchInterval?: number;
    search?: string;
    propertyIds?: number[];
    hasLeads?: boolean | null;
    dateRange?: ListingsDateRange | null;
};

export type IListingGroup = {
    property_id: number;
    listings: IListing[];
};

export type IGetMatchedListingsResponse = {
    groups: IListingGroup[];
    total_properties?: number;
};

const GET_MATCHED_LISTINGS_PATH = 'api/landlord/listings/matched';

export const useMatchedListings = (options?: IGetMatchedListingsOptions) => {
    const queryClient = useQueryClient();

    return useQuery(
        [queryKeys.listings, {
            isMatched: true,
            propertyId: options?.propertyId,
            status: options?.status ?? 'active',
            page: options?.page,
            pageSize: options?.pageSize,
            search: options?.search,
            propertyIds: options?.propertyIds,
            hasLeads: options?.hasLeads,
            dateRange: options?.dateRange,
        }],
        async () => {
            const searchParams = new URLSearchParams();
            if (options?.propertyId) {
                searchParams.set('property_id', String(options.propertyId));
            }
            searchParams.set('status', options?.status ?? 'active');
            if (options?.page !== undefined) {
                searchParams.set('page', String(options.page));
            }
            if (options?.pageSize !== undefined) {
                searchParams.set('page_size', String(options.pageSize));
            }
            if (options?.search) {
                searchParams.set('search', options.search);
            }
            for (const id of options?.propertyIds ?? []) {
                searchParams.append('property_ids', String(id));
            }
            if (options?.hasLeads != null) {
                searchParams.set('has_leads', String(options.hasLeads));
            }
            if (options?.dateRange) {
                searchParams.set('date_range', options.dateRange);
            }

            const res = await apiClient(GET_MATCHED_LISTINGS_PATH, {
                searchParams,
            }).json<IGetMatchedListingsResponse>();
            return res;
        },
        {
            enabled: !options?.disabled,
            keepPreviousData: true,
            refetchInterval: options?.refetchInterval,
            onSuccess: (data) => {
                for (const group of data.groups) {
                    for (const listing of group.listings) {
                        queryClient.setQueryData([queryKeys.listings, listing.id], listing);
                    }
                }
            },
        }
    );
};
